/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { getAllStyleSetsInSelection } from '../draftUtils';
import { EditorState } from 'draft-js';
import {
  getEmptyLineHeightAtCaret,
  getSelectedEmptyLineHeights,
  normalizeSelectedEmptyLines,
  setEmptyLineHeights,
} from '../emptyLineHeight';
import { MULTIPLE_VALUE } from '../../../constants';
import { LINE_HEIGHT, NONE } from '../customConstants';
import {
  getPrefixStylesInSelection,
  togglePrefixStyle,
} from '../styleManipulation';
import { getVariable, isStyle } from './util';
import {
  LINE_HEIGHT_ATTRIBUTE,
  parseInlineLineHeight,
} from '../../../elements/text/utils/mixedTypography';

const canonicalizeLineHeight = (value) => `${Number(value)}`;

export const lineHeightToStyle = (lineHeight) =>
  `${LINE_HEIGHT}-${canonicalizeLineHeight(lineHeight)}`;

export const styleToLineHeight = (style) =>
  parseInlineLineHeight(getVariable(style, LINE_HEIGHT));

function elementToStyle(element) {
  if (element.tagName.toLowerCase() !== 'span') {
    return null;
  }

  const lineHeight = parseInlineLineHeight(
    element.getAttribute(LINE_HEIGHT_ATTRIBUTE)
  );
  return lineHeight === null ? null : lineHeightToStyle(lineHeight);
}

function getLineHeightStyle(styles) {
  return styles.find((style) => isStyle(style, LINE_HEIGHT));
}

function stylesToCSS(styles, context = {}) {
  const style = getLineHeightStyle(styles);
  if (!style) {
    return null;
  }

  const lineHeight = styleToLineHeight(style);
  return lineHeight === null
    ? null
    : { lineHeight: context.lineHeightToCss?.(lineHeight) ?? lineHeight };
}

function stylesToAttributes(styles) {
  const style = getLineHeightStyle(styles);
  if (!style) {
    return null;
  }

  const lineHeight = styleToLineHeight(style);
  return lineHeight === null
    ? null
    : { [LINE_HEIGHT_ATTRIBUTE]: canonicalizeLineHeight(lineHeight) };
}

function getLineHeight(editorState, context) {
  const caretHeight = getEmptyLineHeightAtCaret(
    editorState,
    context?.defaultLineHeight
  );
  if (caretHeight !== null) {
    return caretHeight;
  }
  const emptyValues = getSelectedEmptyLineHeights(
    editorState,
    context?.defaultLineHeight
  );
  const values = new Set(
    getPrefixStylesInSelection(editorState, LINE_HEIGHT).map((style) =>
      style === NONE ? context?.defaultLineHeight : styleToLineHeight(style)
    )
  );

  if (
    emptyValues.length &&
    getAllStyleSetsInSelection(editorState).length === 0
  ) {
    values.clear();
  }
  emptyValues.forEach((value) => values.add(value));
  return values.size > 1 ? MULTIPLE_VALUE : [...values][0];
}

function setLineHeight(editorState, { lineHeight, defaultLineHeight } = {}) {
  const parsedLineHeight = parseInlineLineHeight(lineHeight);
  if (parsedLineHeight === null) {
    return editorState;
  }

  const normalized = normalizeSelectedEmptyLines(editorState);
  const hasEmptyLines =
    getSelectedEmptyLineHeights(normalized, defaultLineHeight).length > 0;
  const styled = togglePrefixStyle(
    normalized,
    LINE_HEIGHT,
    () => parsedLineHeight !== defaultLineHeight,
    () => lineHeightToStyle(parsedLineHeight)
  );
  if (!hasEmptyLines) {
    return styled;
  }
  const content = setEmptyLineHeights(styled, parsedLineHeight).set(
    'selectionAfter',
    normalized.getSelection()
  );
  const next = EditorState.push(editorState, content, 'change-block-data');
  return EditorState.setInlineStyleOverride(
    next,
    styled.getCurrentInlineStyle().add(lineHeightToStyle(parsedLineHeight))
  );
}

function clearLineHeight(editorState) {
  const styled = togglePrefixStyle(editorState, LINE_HEIGHT, () => false);
  if (!getSelectedEmptyLineHeights(editorState).length) {
    return styled;
  }
  return EditorState.push(
    editorState,
    setEmptyLineHeights(styled, null),
    'change-block-data'
  );
}

const formatter = {
  elementToStyle,
  stylesToCSS,
  stylesToAttributes,
  autoFocus: false,
  getters: { lineHeight: getLineHeight },
  setters: { setLineHeight, clearLineHeight },
};

export default formatter;
